import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { FailureNotice } from '@/components/platform/AsyncPanel';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData, type AsyncState } from '@/hooks';
import {
  BULK_PRODUCT_ACTIONS,
  GROUP_ADMIN_ROLE_CODES,
  GROUP_MEMBERSHIP_ROLES,
  INSTITUTION_GROUP_TYPES,
  INVITATION_TYPES,
  institutionDisplayName,
} from '@/types/platform';
import type {
  BulkProductAction,
  CreateGroupRequest,
  InstitutionGroup,
  InstitutionGroupType,
  InvitationType,
  PlatformInstitutionRecord,
  ProductCatalogueEntry,
} from '@/types/platform';
import styles from './platform-pages.module.css';

/**
 * The group onboarding wizard — creating a group and bringing it to life in
 * one pass, instead of five dialogs spread across five visits.
 *
 * ONE COMMIT, STATED AS SUCH. The wizard collects the whole onboarding up
 * front and executes it in a fixed order on the review step: the group is
 * created first (nothing can reference it before it exists), then the member
 * rows, then the administrator invitations, then the product action. If any
 * step fails, the error names the group the wizard had already created rather
 * than pretending nothing happened — the review screen is the operator's
 * record of what was asked for, and the error says how far it got.
 *
 * THE OPTIONAL STEPS ARE OPTIONAL ON PURPOSE. A school group is often born
 * with its schools; an education authority is often born with its people
 * first. Neither is required, and an empty step is not an error — the review
 * screen says "none" so the operator sees the choice was made, not skipped
 * by accident.
 */

const STEP_KEYS = ['details', 'institutions', 'administrators', 'products', 'review'] as const;

export function GroupOnboardingWizard({
  open,
  onClose,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted: (group: InstitutionGroup) => void;
}) {
  const { t } = useTranslation();

  // ── Step 1: details ──
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<InstitutionGroupType>(INSTITUTION_GROUP_TYPES[0]);
  const [description, setDescription] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [tenantId, setTenantId] = useState('');

  // ── Step 2: institutions ──
  const [step, setStep] = useState(0);
  const [institutionIds, setInstitutionIds] = useState<string[]>([]);
  // MEMBER is the ordinary seat — the wizard's own copy says so, so it is also
  // the seat it starts on. HEAD_OFFICE is a designation, not a default.
  const [memberRole, setMemberRole] = useState<GroupOnboardingRole>('MEMBER');

  // ── Step 3: administrators ──
  const [admins, setAdmins] = useState<WizardAdmin[]>([]);

  // ── Step 4: products ──
  const [productCode, setProductCode] = useState('');
  const [productAction, setProductAction] = useState<BulkProductAction>(BULK_PRODUCT_ACTIONS[0]);

  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const institutionsFetcher = useCallback(() => platformApi.institutions.list(), []);
  const { state: institutionsState, reload: reloadInstitutions } = useAsyncData<PlatformInstitutionRecord[]>(
    'onboard-institutions',
    institutionsFetcher,
    open && step >= 1,
  );

  const catalogueFetcher = useCallback(() => platformApi.products.catalogue(), []);
  const { state: catalogueState, reload: reloadCatalogue } = useAsyncData<ProductCatalogueEntry[]>(
    'onboard-catalogue',
    catalogueFetcher,
    open && step >= 3,
  );

  const codeValid = /^[A-Z0-9][A-Z0-9-]{1,48}[A-Z0-9]$/.test(code.trim());
  const detailsValid = codeValid && name.trim().length > 0;
  const adminsValid = admins.every((admin) => adminValid(admin.email, admin.firstName, admin.lastName));

  const productName = catalogueState.status === 'ready'
    ? catalogueState.data.find((entry) => entry.code === productCode)?.name ?? productCode
    : productCode;

  const next = () => {
    if (step === 0 && !detailsValid) {
      setError(t('platform.groups.onboard.stepError'));
      return;
    }
    if (step === 2 && !adminsValid) {
      setError(t('platform.groups.onboard.stepError'));
      return;
    }
    setError(null);
    setStep(Math.min(step + 1, STEP_KEYS.length - 1));
  };

  const back = () => {
    setError(null);
    setStep(Math.max(step - 1, 0));
  };

  const close = () => {
    if (executing) return;
    onClose();
    // The next onboarding starts clean — the previous run's group, members,
    // invites and product are all in the past.
    setStep(0);
    setCode('');
    setName('');
    setType(INSTITUTION_GROUP_TYPES[0]);
    setDescription('');
    setCountryCode('');
    setTenantId('');
    setInstitutionIds([]);
    setMemberRole('MEMBER');
    setAdmins([]);
    setProductCode('');
    setProductAction(BULK_PRODUCT_ACTIONS[0]);
    setError(null);
  };

  const execute = async () => {
    if (executing) return;
    setExecuting(true);
    setError(null);
    try {
      const payload: CreateGroupRequest = {
        code: code.trim(),
        name: name.trim(),
        type,
        description: description.trim() || undefined,
        countryCode: countryCode.trim() || undefined,
        ...(tenantId.trim() ? { tenantId: tenantId.trim() } : {}),
      };
      const group = await platformApi.structure.createGroup(payload);

      await Promise.all(
        institutionIds.map((institutionId) => platformApi.structure.addGroupMember(group.id, institutionId, memberRole)),
      );

      await Promise.all(
        admins.map((admin) =>
          platformApi.administrators.invite(
            undefined,
            {
              email: admin.email.trim(),
              firstName: admin.firstName.trim(),
              lastName: admin.lastName.trim(),
              ...(admin.phone.trim() ? { phone: admin.phone.trim() } : {}),
              invitationType: admin.invitationType,
              initialRoleNames: [admin.roleName],
            },
            group.id,
          ),
        ),
      );

      if (productCode) {
        await platformApi.structure.bulkApplyGroupProduct(group.id, { productCode, action: productAction });
      }

      onCompleted(group);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setExecuting(false);
    }
  };

  const stepLabel = (key: (typeof STEP_KEYS)[number]) => t(`platform.groups.onboard.step.${key}`);

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('platform.groups.onboard.title')}
      description={t('platform.groups.onboard.lead')}
      closeOnBackdropClick={false}
    >
      <p className={styles.fieldHint} role="status">
        {t('platform.groups.onboard.stepsAria', { current: step + 1, total: STEP_KEYS.length, label: stepLabel(STEP_KEYS[step]) })}
      </p>
      <ol className={styles.wizardSteps} aria-label={t('platform.groups.onboard.stepsAria', { current: step + 1, total: STEP_KEYS.length, label: stepLabel(STEP_KEYS[step]) })}>
        {STEP_KEYS.map((key, index) => (
          <li key={key} className={index === step ? styles.wizardStepActive : undefined}>
            <button
              type="button"
              disabled={index === step}
              onClick={() => {
                if (index < step) {
                  setError(null);
                  setStep(index);
                }
              }}
            >
              <span className={styles.wizardStepNumber}>{index + 1}</span>
              {stepLabel(key)}
            </button>
          </li>
        ))}
      </ol>

      {step === 0 && (
        <DetailsStep
          code={code}
          setCode={setCode}
          name={name}
          setName={setName}
          type={type}
          setType={setType}
          description={description}
          setDescription={setDescription}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
          tenantId={tenantId}
          setTenantId={setTenantId}
          codeValid={codeValid}
        />
      )}

      {step === 1 && (
        <InstitutionsStep
          state={institutionsState}
          onRetry={reloadInstitutions}
          institutionIds={institutionIds}
          setInstitutionIds={setInstitutionIds}
          memberRole={memberRole}
          setMemberRole={setMemberRole}
        />
      )}

      {step === 2 && (
        <AdministratorsStep admins={admins} setAdmins={setAdmins} />
      )}

      {step === 3 && (
        <ProductsStep
          state={catalogueState}
          onRetry={reloadCatalogue}
          productCode={productCode}
          setProductCode={setProductCode}
          productAction={productAction}
          setProductAction={setProductAction}
        />
      )}

      {step === 4 && (
        <ReviewStep
          name={name}
          code={code}
          type={type}
          institutionsState={institutionsState}
          institutionIds={institutionIds}
          admins={admins}
          productName={productCode ? productName : null}
          productAction={productCode ? productAction : null}
        />
      )}

      {error && (
        <p className={styles.formError} role="alert">
          {error}
        </p>
      )}

      <div className="modal-actions">
        {step > 0 ? (
          <button type="button" className="modal-button modal-button--secondary" onClick={back} disabled={executing}>
            {t('platform.groups.onboard.back')}
          </button>
        ) : (
          <button type="button" className="modal-button modal-button--secondary" onClick={close} disabled={executing}>
            {t('common.cancel')}
          </button>
        )}

        {step < STEP_KEYS.length - 1 && (
          <>
            {step > 0 && step < STEP_KEYS.length - 1 && (
              <button type="button" className={styles.linkBtn} onClick={next} disabled={executing}>
                {t('platform.groups.onboard.skip')}
              </button>
            )}
            <button type="button" className="modal-button" onClick={next} disabled={executing}>
              {t('platform.groups.onboard.next')}
            </button>
          </>
        )}

        {step === STEP_KEYS.length - 1 && (
          <button type="button" className="modal-button" onClick={execute} disabled={executing}>
            {executing ? t('platform.groups.onboard.executing') : t('platform.groups.onboard.execute')}
          </button>
        )}
      </div>
    </Modal>
  );
}

type GroupOnboardingRole = (typeof GROUP_MEMBERSHIP_ROLES)[number];

interface WizardAdmin {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  invitationType: InvitationType;
  roleName: string;
}

function adminValid(email: string, firstName: string, lastName: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) && firstName.trim().length > 0 && lastName.trim().length > 0;
}

/**
 * Step 1 — the group's own record.
 *
 * The same fields the create dialog always had, in the same order and with
 * the same validation. Nothing here is optional beyond what core accepts.
 */
function DetailsStep({
  code,
  setCode,
  name,
  setName,
  type,
  setType,
  description,
  setDescription,
  countryCode,
  setCountryCode,
  tenantId,
  setTenantId,
  codeValid,
}: {
  code: string;
  setCode: (value: string) => void;
  name: string;
  setName: (value: string) => void;
  type: InstitutionGroupType;
  setType: (value: InstitutionGroupType) => void;
  description: string;
  setDescription: (value: string) => void;
  countryCode: string;
  setCountryCode: (value: string) => void;
  tenantId: string;
  setTenantId: (value: string) => void;
  codeValid: boolean;
}) {
  const { t } = useTranslation();
  return (
    <form className={styles.dialogForm} noValidate onSubmit={(event) => event.preventDefault()}>
      <p className={styles.fieldHint}>{t('platform.groups.onboard.detailsLead')}</p>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t('platform.groups.fieldName')}</span>
        <input className={styles.input} value={name} required onChange={(event) => setName(event.target.value)} />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t('platform.groups.fieldCode')}</span>
        <input
          className={styles.input}
          value={code}
          required
          aria-invalid={code.length > 0 && !codeValid}
          aria-describedby="onboard-code-hint"
          onChange={(event) => setCode(event.target.value.toUpperCase())}
        />
        <span className={styles.fieldHint} id="onboard-code-hint">
          {t('platform.groups.fieldCodeHint')}
        </span>
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t('platform.groups.fieldType')}</span>
        <Select
          value={type}
          onChange={(value) => setType(value as InstitutionGroupType)}
          options={INSTITUTION_GROUP_TYPES.map((candidate) => ({
            value: candidate,
            label: t(`platform.structure.groupType.${candidate}`, { defaultValue: candidate }),
          }))}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t('platform.groups.fieldDescription')}</span>
        <textarea
          className={styles.textarea}
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <div className={styles.dialogRow}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.fieldCountry')}</span>
          <input
            className={styles.input}
            value={countryCode}
            maxLength={2}
            onChange={(event) => setCountryCode(event.target.value.toUpperCase())}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.fieldTenant')}</span>
          <input
            className={styles.input}
            value={tenantId}
            aria-describedby="onboard-tenant-hint"
            onChange={(event) => setTenantId(event.target.value)}
          />
          <span className={styles.fieldHint} id="onboard-tenant-hint">
            {t('platform.groups.fieldTenantHint')}
          </span>
        </label>
      </div>
    </form>
  );
}

/**
 * Step 2 — the institutions that will sit in the group.
 *
 * The register comes from the same institutions surface the add-member dialog
 * uses, and the role select carries the membership vocabulary core accepts.
 * The membership rows are created only when the wizard executes — this step
 * merely collects the intention.
 */
function InstitutionsStep({
  state,
  onRetry,
  institutionIds,
  setInstitutionIds,
  memberRole,
  setMemberRole,
}: {
  state: AsyncState<PlatformInstitutionRecord[]>;
  onRetry: () => void;
  institutionIds: string[];
  setInstitutionIds: (ids: string[]) => void;
  memberRole: GroupOnboardingRole;
  setMemberRole: (role: GroupOnboardingRole) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  if (state.status === 'loading') {
    return (
      <p className={styles.sectionEmpty} role="status">
        {t('common.loading')}
      </p>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('platform.institutions.registryMissing')}
        </p>
        <FailureNotice error={state.error} onRetry={onRetry} />
      </>
    );
  }

  const q = query.trim().toLowerCase();
  const visible = q
    ? state.data.filter(
        (institution) =>
          institutionDisplayName(institution).toLowerCase().includes(q) ||
          (institution.code ?? '').toLowerCase().includes(q),
      )
    : state.data;

  const toggle = (id: string) => {
    setInstitutionIds(institutionIds.includes(id) ? institutionIds.filter((candidate) => candidate !== id) : [...institutionIds, id]);
  };

  return (
    <form className={styles.dialogForm} noValidate onSubmit={(event) => event.preventDefault()}>
      <h3 className={styles.subheading}>{t('platform.groups.onboard.institutionsTitle')}</h3>
      <p className={styles.fieldHint}>{t('platform.groups.onboard.institutionsLead')}</p>

      <input
        className={styles.input}
        value={query}
        placeholder={t('platform.groups.onboard.institutionsPlaceholder')}
        onChange={(event) => setQuery(event.target.value)}
      />

      <ul className={styles.checklist} role="group" aria-label={t('platform.groups.onboard.institutionsTitle')}>
        {visible.length === 0 ? (
          <li className={styles.rowDetail}>{t('common.noResults')}</li>
        ) : (
          visible.map((institution) => (
            <li key={institution.id} className={styles.checklistItem}>
              <label className={styles.checklistLabel}>
                <input
                  type="checkbox"
                  checked={institutionIds.includes(institution.id)}
                  onChange={() => toggle(institution.id)}
                />
                <span>
                  {institutionDisplayName(institution)}
                  <span className={styles.checklistMeta}>{institution.code}</span>
                </span>
              </label>
            </li>
          ))
        )}
      </ul>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t('platform.groups.onboard.fieldRole')}</span>
        <Select
          value={memberRole}
          onChange={(value) => setMemberRole(value as GroupOnboardingRole)}
          options={GROUP_MEMBERSHIP_ROLES.map((candidate) => ({
            value: candidate,
            label: t(`platform.structure.membership.${candidate}`, { defaultValue: candidate }),
          }))}
        />
        <span className={styles.fieldHint}>{t('platform.groups.onboard.fieldRoleHint')}</span>
      </label>

      <p className={styles.fieldHint}>{t('platform.groups.onboard.selected', { n: institutionIds.length })}</p>
    </form>
  );
}

/**
 * Step 3 — the people who will run the group.
 *
 * Each row mirrors the invite dialog's fields, with the same validation
 * applied up front; the invitations are sent when the wizard executes. A
 * partial row blocks the step — an invitation sent without a surname would
 * come back from identity as a 400, and this wizard refuses to discover that
 * at execution time.
 */
function AdministratorsStep({ admins, setAdmins }: { admins: WizardAdmin[]; setAdmins: (admins: WizardAdmin[]) => void }) {
  const { t } = useTranslation();

  const emptyAdmin = (): WizardAdmin => ({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    invitationType: INVITATION_TYPES.find((candidate) => candidate !== 'STANDARD') ?? INVITATION_TYPES[0],
    roleName: GROUP_ADMIN_ROLE_CODES[0] as string,
  });

  const update = (index: number, patch: Partial<WizardAdmin>) => {
    setAdmins(admins.map((admin, i) => (i === index ? { ...admin, ...patch } : admin)));
  };

  const remove = (index: number) => {
    setAdmins(admins.filter((_, i) => i !== index));
  };

  return (
    <form className={styles.dialogForm} noValidate onSubmit={(event) => event.preventDefault()}>
      <h3 className={styles.subheading}>{t('platform.groups.onboard.adminsTitle')}</h3>
      <p className={styles.fieldHint}>{t('platform.groups.onboard.adminsLead')}</p>

      {admins.map((admin, index) => (
        <fieldset key={index} className={styles.wizardAdminRow}>
          <legend className={styles.fieldLabel}>
            {t('platform.groups.admins.title')} {index + 1}
          </legend>
          <div className={styles.dialogRow}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldEmail')}</span>
              <input
                className={styles.input}
                type="email"
                value={admin.email}
                required
                aria-invalid={admin.email.length > 0 && !adminValid(admin.email, admin.firstName, admin.lastName)}
                onChange={(event) => update(index, { email: event.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldFirstName')}</span>
              <input
                className={styles.input}
                value={admin.firstName}
                required
                onChange={(event) => update(index, { firstName: event.target.value })}
              />
            </label>
          </div>
          <div className={styles.dialogRow}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldLastName')}</span>
              <input
                className={styles.input}
                value={admin.lastName}
                required
                onChange={(event) => update(index, { lastName: event.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldPhone')}</span>
              <input
                className={styles.input}
                type="tel"
                value={admin.phone}
                onChange={(event) => update(index, { phone: event.target.value })}
              />
            </label>
          </div>
          <div className={styles.dialogRow}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldType')}</span>
              <Select
                value={admin.invitationType}
                onChange={(value) => update(index, { invitationType: value as InvitationType })}
                options={INVITATION_TYPES.filter((type) => type !== 'STANDARD').map((type) => ({
                  value: type,
                  label: t(`platform.admins.invitationType.${type}`, { defaultValue: type }),
                }))}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldRole')}</span>
              <Select
                value={admin.roleName}
                onChange={(value) => update(index, { roleName: value })}
                options={GROUP_ADMIN_ROLE_CODES.map((code) => ({
                  value: code,
                  label: t(`platform.groups.admins.role.${code}`, { defaultValue: code }),
                }))}
              />
            </label>
          </div>
          <button type="button" className={styles.linkBtnDanger} onClick={() => remove(index)}>
            {t('platform.groups.onboard.removeAdmin')}
          </button>
        </fieldset>
      ))}

      <button type="button" className={styles.linkBtn} onClick={() => setAdmins([...admins, emptyAdmin()])}>
        <Icon name="plus" size={14} />
        {t('platform.groups.onboard.addAdmin')}
      </button>

      {admins.length === 0 && <p className={styles.fieldHint}>{t('platform.groups.onboard.noAdmins')}</p>}
    </form>
  );
}

/**
 * Step 4 — the one product action to land on the group's members at birth.
 *
 * Kept deliberately single-action: a product and a verb, applied to every
 * member the group is about to get. The bulk endpoint answers with the
 * per-member report, which is the correct shape for one action; a wizard
 * building a whole product portfolio belongs to another phase.
 */
function ProductsStep({
  state,
  onRetry,
  productCode,
  setProductCode,
  productAction,
  setProductAction,
}: {
  state: AsyncState<ProductCatalogueEntry[]>;
  onRetry: () => void;
  productCode: string;
  setProductCode: (code: string) => void;
  productAction: BulkProductAction;
  setProductAction: (action: BulkProductAction) => void;
}) {
  const { t } = useTranslation();

  if (state.status === 'loading') {
    return (
      <p className={styles.sectionEmpty} role="status">
        {t('common.loading')}
      </p>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('platform.groups.onboard.stepError')}
        </p>
        <FailureNotice error={state.error} onRetry={onRetry} />
      </>
    );
  }

  return (
    <form className={styles.dialogForm} noValidate onSubmit={(event) => event.preventDefault()}>
      <h3 className={styles.subheading}>{t('platform.groups.onboard.productsTitle')}</h3>
      <p className={styles.fieldHint}>{t('platform.groups.onboard.productsLead')}</p>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t('platform.groups.onboard.productLabel')}</span>
        <Select
          searchable
          value={productCode}
          onChange={setProductCode}
          placeholder={t('platform.groups.onboard.productPlaceholder')}
          options={state.data.map((entry) => ({ value: entry.code, label: entry.name, description: entry.code }))}
        />
      </label>

      <div className={styles.dialogRow}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.products.actionLabel')}</span>
          <Select
            value={productAction}
            onChange={(value) => setProductAction(value as BulkProductAction)}
            options={BULK_PRODUCT_ACTIONS.map((candidate) => ({
              value: candidate,
              label:
                candidate === 'SUBSCRIBE'
                  ? t('platform.groups.products.actionSubscribe')
                  : t('platform.groups.products.actionUnsubscribe'),
            }))}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.onboard.productScope')}</span>
          <input className={styles.input} value={t('platform.groups.onboard.productScopeAll')} disabled />
        </label>
      </div>

      {!productCode && <p className={styles.fieldHint}>{t('platform.groups.onboard.noProduct')}</p>}
    </form>
  );
}

/**
 * Step 5 — the whole onboarding on one screen, before a single request goes
 * out. What is listed here is exactly what the execution order will do; a
 * "none" is a stated choice, not a blank.
 */
function ReviewStep({
  name,
  code,
  type,
  institutionsState,
  institutionIds,
  admins,
  productName,
  productAction,
}: {
  name: string;
  code: string;
  type: InstitutionGroupType;
  institutionsState: AsyncState<PlatformInstitutionRecord[]>;
  institutionIds: string[];
  admins: WizardAdmin[];
  productName: string | null;
  productAction: BulkProductAction | null;
}) {
  const { t } = useTranslation();

  const institutionName = (id: string): string => {
    if (institutionsState.status !== 'ready') return id;
    const record = institutionsState.data.find((institution) => institution.id === id);
    return record ? institutionDisplayName(record) : id;
  };

  return (
    <div className={styles.dialogForm}>
      <h3 className={styles.subheading}>{t('platform.groups.onboard.reviewTitle')}</h3>
      <p className={styles.fieldHint}>{t('platform.groups.onboard.reviewLead')}</p>

      <div className={styles.detailGrid}>
        <dt>{t('platform.groups.onboard.reviewGroup')}</dt>
        <dd>
          {name}
          <span className={styles.tableSub}>
            {' '}
            {code} · {t(`platform.structure.groupType.${type}`, { defaultValue: type })}
          </span>
        </dd>

        <dt>{t('platform.groups.onboard.reviewInstitutions')}</dt>
        <dd>
          {institutionIds.length === 0 ? (
            t('platform.groups.onboard.none')
          ) : (
            <span className={styles.rowDetail}>{institutionIds.map(institutionName).join(', ')}</span>
          )}
        </dd>

        <dt>{t('platform.groups.onboard.reviewAdministrators')}</dt>
        <dd>
          {admins.length === 0 ? (
            t('platform.groups.onboard.none')
          ) : (
            <span className={styles.rowDetail}>{admins.map((admin) => admin.email).join(', ')}</span>
          )}
        </dd>

        <dt>{t('platform.groups.onboard.reviewProducts')}</dt>
        <dd>
          {productName === null ? (
            t('platform.groups.onboard.none')
          ) : (
            <span className={styles.rowDetail}>
              {t(
                productAction === 'SUBSCRIBE'
                  ? 'platform.groups.products.actionSubscribe'
                  : 'platform.groups.products.actionUnsubscribe',
              )}{' '}
              — {productName}
            </span>
          )}
        </dd>
      </div>
    </div>
  );
}
